import React, { useState, useEffect } from "react";
// WhatsApp upload improvements - backend integration working with enhanced error handling
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  CircularProgress,
  Chip,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideocamIcon from "@mui/icons-material/Videocam";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { useAuth } from "../contexts/AuthContext";
import {
  BlogMediaType,
  BlogMediaItem,
  validateMediaItem,
  BlogPost,
} from "../utils/mediaHelpers";
import TranscribeButton from "./TranscribeButton";

interface BlogCreateProps {
  initialValues?: Partial<BlogPost>;
  isEdit?: boolean;
  onSuccess?: () => void;
  onSubmit?: (values: Partial<BlogPost>) => Promise<void>;
}

const BlogCreate: React.FC<BlogCreateProps> = ({
  initialValues,
  isEdit,
  onSuccess,
  onSubmit,
}) => {
  const { user } = useAuth();
  const [title, setTitle] = useState(initialValues?.title || "");
  const [content, setContent] = useState(initialValues?.content || "");
  const [category, setCategory] = useState(initialValues?.category || "News");
  const [media, setMedia] = useState<BlogMediaItem[]>(
    initialValues?.media || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [mediaType, setMediaType] = useState<BlogMediaType>("photo");
  const [mediaHeading, setMediaHeading] = useState("");
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(
    null
  );
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<string>("");
  const [importingMarkdown, setImportingMarkdown] = useState(false);
  const [markdownImportError, setMarkdownImportError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (initialValues) {
      setTitle(initialValues.title || "");
      setContent(initialValues.content || "");
      setCategory(initialValues.category || "News");

      // Handle media conversion from legacy format to unified format
      let mediaItems = initialValues.media || [];

      // If no unified media but legacy media exists, convert it
      if (!mediaItems || mediaItems.length === 0) {
        mediaItems = [];

        // Convert photos
        if (initialValues.photos && initialValues.photos.length > 0) {
          initialValues.photos.forEach((photo: any) => {
            mediaItems.push({
              type: "photo",
              data: {
                url: photo.url,
                heading: photo.caption || "",
              },
            });
          });
        }

        // Convert videos
        if (initialValues.videos && initialValues.videos.length > 0) {
          initialValues.videos.forEach((video: any) => {
            mediaItems.push({
              type: "video",
              data: {
                url: video.url,
                heading: video.caption || "",
              },
            });
          });
        }

        // Convert PDFs
        if (initialValues.pdfs && initialValues.pdfs.length > 0) {
          initialValues.pdfs.forEach((pdf: any) => {
            mediaItems.push({
              type: "pdf",
              data: {
                url: pdf.url,
                heading: pdf.caption || "",
              },
            });
          });
        }

        // Convert audios
        if (initialValues.audios && initialValues.audios.length > 0) {
          initialValues.audios.forEach((audio: any) => {
            mediaItems.push({
              type: "audio",
              data: {
                url: audio.url,
                heading: audio.caption || "",
                transcript: audio.transcript || "",
              },
            });
          });
        }
      }

      setMedia(mediaItems);
    }
  }, [initialValues]);

  // Debug effect to monitor media state changes
  useEffect(() => {
    console.log(`🔄 Media state changed: ${media.length} items`);
  }, [media]);

  if (!user) return null;

  // File validation
  const validateFile = (file: File, type: BlogMediaType): string | null => {
    const maxSizes = {
      photo: 10 * 1024 * 1024, // 10MB
      video: 100 * 1024 * 1024, // 100MB
      pdf: 1000 * 1024 * 1024, // 1GB (increased for large architectural plans)
      audio: 50 * 1024 * 1024, // 50MB
    };

    if (file.size > maxSizes[type]) {
      return `File size must be less than ${Math.round(
        maxSizes[type] / (1024 * 1024)
      )}MB`;
    }

    return null;
  };

  // Helper: Upload a file to a given endpoint with retry logic
  const uploadFile = async (
    file: File,
    endpoint: string,
    heading: string,
    skipTranscription: boolean = false,
    retryCount: number = 0
  ): Promise<any> => {
    // Set timeout for large files (20 minutes for files > 100MB, 15 minutes for files > 50MB, 10 minutes for audio files, 3 minutes for others)
    const timeout =
      file.size > 100 * 1024 * 1024
        ? 1200000
        : file.size > 50 * 1024 * 1024
        ? 900000
        : endpoint.includes("/audio")
        ? 900000 // 15 minutes for ALL audio files (transcription is slow regardless of size)
        : 180000; // 3 minutes for other files
    console.log(`Starting file upload to ${endpoint}`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      heading: heading,
      skipTranscription: skipTranscription,
      timeout: `${timeout / 1000}s`,
      isAudio: endpoint.includes("/audio"),
    });

    const formData = new FormData();
    formData.append("file", file);
    if (endpoint === "/api/upload/audio") {
      formData.append("heading", heading);
      if (skipTranscription) {
        formData.append("skip_transcription", "true");
      }
    }

    const fileId = `${file.name}-${Date.now()}`;
    setUploadingFiles((prev) => new Set(prev).add(fileId));

    try {
      console.log(`Sending request to ${endpoint}`);

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () =>
            reject(new Error(`Upload timeout after ${timeout / 1000} seconds`)),
          timeout
        );
      });

      // Create the fetch promise
      const fetchPromise = fetch(endpoint, {
        method: "POST",
        body: formData,
      });

      // Race between fetch and timeout
      const res = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as Response;
      console.log(`Response status: ${res.status}`);
      console.log(
        `Response headers:`,
        Object.fromEntries(res.headers.entries())
      );

      const data = await res.json();
      console.log(`Response data:`, data);

      if (!res.ok) {
        let errorMessage =
          data.detail ||
          data.message ||
          `Upload failed: ${res.status} ${res.statusText}`;

        // Provide more specific error messages for common issues
        if (res.status === 400) {
          if (errorMessage.includes("too large")) {
            errorMessage = `File too large: ${file.name} (max 100MB for videos, 500MB for PDFs)`;
          } else if (errorMessage.includes("Invalid file type")) {
            errorMessage = `Unsupported file type: ${file.name}`;
          } else if (errorMessage.includes("too small")) {
            errorMessage = `File appears corrupted: ${file.name} (file too small)`;
          } else if (errorMessage.includes("corrupted")) {
            errorMessage = `File appears corrupted: ${file.name}`;
          }
        } else if (res.status === 413) {
          errorMessage = `File too large: ${file.name} (server limit exceeded)`;
        } else if (res.status === 500) {
          errorMessage = `Server error while uploading: ${file.name}`;
        } else if (res.status === 507) {
          errorMessage = `Server disk space full. Please try again later.`;
        }

        console.error(`Upload failed for ${file.name}: ${errorMessage}`);
        throw new Error(errorMessage);
      }
      console.log(`Upload successful to ${endpoint}`);
      return data;
    } catch (error) {
      console.error(`Upload error to ${endpoint}:`, error);

      // Retry logic for timeout errors (up to 4 retries for audio, 2 for others)
      const maxRetries = endpoint.includes("/audio") ? 4 : 2;
      const isTimeoutError =
        error instanceof Error && error.message.includes("timeout");

      if (isTimeoutError && retryCount < maxRetries) {
        console.log(
          `Retrying upload for ${file.name} (attempt ${
            retryCount + 1
          }/${maxRetries})`
        );
        // Wait longer for audio files (5 seconds) vs others (2 seconds)
        const retryDelay = endpoint.includes("/audio") ? 5000 : 2000;
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return uploadFile(
          file,
          endpoint,
          heading,
          skipTranscription,
          retryCount + 1
        );
      }

      throw error;
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  // Helper: Parse WhatsApp chat txt file with improved chronological ordering - VERSION 5.0 NUCLEAR CACHE BUST
  const parseWhatsAppChatWithMediaV5 = (
    text: string
  ): {
    title: string;
    parts: Array<{
      type: "text" | "media";
      content: string;
      timestamp?: string;
      date?: string;
      time?: string;
      sender?: string;
      isForwarded?: boolean;
      originalLine?: string;
      fullTimestamp?: string;
    }>;
  } => {
    const removeDirectionMarks = (value: string) =>
      value.replace(/[\u200e\u202a\u202c\u202d\u202e]/g, "");

    const pad = (value: number) => value.toString().padStart(2, "0");

    const parseTime = (rawTime?: string) => {
      if (!rawTime) {
        return {
          hour: 0,
          minute: 0,
          second: 0,
          display: undefined as string | undefined,
        };
      }

      const normalized = removeDirectionMarks(rawTime)
        .replace(/\u202f|\u00a0/g, " ")
        .replace(/a\.\s*m\.?/gi, "AM")
        .replace(/p\.\s*m\.?/gi, "PM")
        .replace(/\.?([AP]M)$/i, "$1")
        .trim();

      const match = normalized.match(
        /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i
      );

      if (!match) {
        return {
          hour: 0,
          minute: 0,
          second: 0,
          display: normalized || undefined,
        };
      }

      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      const second = match[3] ? parseInt(match[3], 10) : 0;
      const period = match[4]?.toUpperCase();

      if (period === "PM" && hour < 12) hour += 12;
      if (period === "AM" && hour === 12) hour = 0;

      const display =
        period != null
          ? `${pad(((hour + 11) % 12) + 1)}:${match[2]}${
              match[3] ? `:${match[3]}` : ""
            } ${period}`
          : `${pad(hour)}:${match[2]}${match[3] ? `:${match[3]}` : ""}`;

      return { hour, minute, second, display };
    };

    const parseDateTime = (datePart?: string, timePart?: string) => {
      if (!datePart) {
        const timeInfo = parseTime(timePart);
        return {
          isoDate: undefined as string | undefined,
          isoTimestamp: undefined as string | undefined,
          displayTime: timeInfo.display,
        };
      }

      const sanitizedDate = removeDirectionMarks(datePart).trim();
      const dateTokens = sanitizedDate.split(/[/\-.]/).map((token) => {
        const parsed = parseInt(token, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      });

      if (dateTokens.length < 3 || dateTokens.includes(undefined)) {
        const timeInfo = parseTime(timePart);
        return {
          isoDate: undefined,
          isoTimestamp: undefined,
          displayTime: timeInfo.display,
        };
      }

      let [first, second, third] = dateTokens as number[];

      let year = third;
      if (year < 100) {
        year += year >= 70 ? 1900 : 2000;
      }

      let day = first;
      let month = second;

      if (first > 12 && second <= 12) {
        day = first;
        month = second;
      } else if (second > 12 && first <= 12) {
        day = second;
        month = first;
      } else if (first <= 12 && second <= 12) {
        // Ambiguous – assume day/month by default
        day = first;
        month = second;
      }

      const timeInfo = parseTime(timePart);

      const normalizedMonth = Math.min(Math.max(month, 1), 12);
      const normalizedDay = Math.min(Math.max(day, 1), 31);

      const date = new Date(
        Date.UTC(
          year,
          Math.min(11, Math.max(0, normalizedMonth - 1)),
          normalizedDay,
          timeInfo.hour,
          timeInfo.minute,
          timeInfo.second
        )
      );

      if (Number.isNaN(date.getTime())) {
        return {
          isoDate: undefined,
          isoTimestamp: undefined,
          displayTime: timeInfo.display,
        };
      }

      return {
        isoDate: `${year}-${pad(normalizedMonth)}-${pad(normalizedDay)}`,
        isoTimestamp: date.toISOString(),
        displayTime: timeInfo.display,
      };
    };

    const extractMedia = (message: string) => {
      const normalized = removeDirectionMarks(message);
      const attachmentPatterns: Array<RegExp> = [
        /<attached:\s*([^>]+)>/i,
        /(?:^|\s)([A-Za-z0-9_\-() ]+\.(?:jpe?g|png|gif|webp|mp4|mov|avi|mkv|flv|webm|pdf|docx?|xlsx?|pptx?|zip|rar|7z|mp3|wav|ogg|opus|aac))(?=\s|$)/i,
        /(\d{8}-WA\d+-[A-Z]+-\d{2}-\d{2}-\d{2}\.[A-Za-z0-9]+)/i,
      ];

      for (const pattern of attachmentPatterns) {
        const match = normalized.match(pattern);
        if (match) {
          const reference = match[1]?.trim() || match[0].trim();
          const remainder = normalized
            .replace(match[0], "")
            .replace(
              /\((?:file|archivo|arquivos?) (?:attached|adjunto|anexo)\)/gi,
              ""
            )
            .trim();
          return {
            reference,
            remainder,
          };
        }
      }

      return null;
    };

    type ParsedMessage = {
      datePart?: string;
      timePart?: string;
      sender?: string;
      content: string;
      originalLine: string;
    };

    const entries: ParsedMessage[] = [];
    let currentEntry: ParsedMessage | null = null;
    let firstSender = "";

    const commitNewEntry = (entry: ParsedMessage) => {
      entries.push(entry);
      currentEntry = entry;
      if (!firstSender && entry.sender) {
        firstSender = entry.sender;
      }
    };

    const splitOnDash = (line: string) => {
      const enDashIndex = line.indexOf(" – ");
      if (enDashIndex >= 0) {
        return {
          left: line.slice(0, enDashIndex),
          right: line.slice(enDashIndex + 3),
        };
      }
      const dashIndex = line.indexOf(" - ");
      if (dashIndex >= 0) {
        return {
          left: line.slice(0, dashIndex),
          right: line.slice(dashIndex + 3),
        };
      }
      return null;
    };

    const lines = text.split(/\r?\n/);
    for (const rawLine of lines) {
      const trimmedLine = removeDirectionMarks(rawLine).trim();
      if (!trimmedLine) {
        continue;
      }

      let timestampPart: string | undefined;
      let messagePart: string | undefined;

      if (trimmedLine.startsWith("[")) {
        const closingBracketIndex = trimmedLine.indexOf("] ");
        if (closingBracketIndex > 0) {
          timestampPart = trimmedLine.slice(1, closingBracketIndex);
          messagePart = trimmedLine.slice(closingBracketIndex + 2);
        }
      }

      if (!timestampPart || messagePart == null) {
        const split = splitOnDash(trimmedLine);
        if (split) {
          timestampPart = split.left;
          messagePart = split.right;
        }
      }

      if (!timestampPart || messagePart == null) {
        if (currentEntry) {
          const updatedEntry: ParsedMessage = {
            ...currentEntry,
            content: `${currentEntry.content}\n${trimmedLine}`,
            originalLine: `${currentEntry.originalLine}\n${rawLine}`,
          };
          if (entries.length > 0) {
            entries[entries.length - 1] = updatedEntry;
          } else {
            entries.push(updatedEntry);
          }
          currentEntry = updatedEntry;
        } else {
          commitNewEntry({
            content: trimmedLine,
            originalLine: rawLine,
          });
        }
        continue;
      }

      const commaIndex = timestampPart.indexOf(",");
      let datePart: string | undefined;
      let timePart: string | undefined;

      if (commaIndex >= 0) {
        datePart = timestampPart.slice(0, commaIndex).trim();
        timePart = timestampPart.slice(commaIndex + 1).trim();
      } else {
        const segments = timestampPart.trim().split(/\s+/);
        if (segments.length >= 2) {
          datePart = segments[0];
          timePart = segments.slice(1).join(" ");
        } else {
          datePart = timestampPart.trim();
        }
      }

      const colonIndex = messagePart.indexOf(":");
      let sender: string | undefined;
      let content = messagePart;

      if (colonIndex >= 0) {
        sender = removeDirectionMarks(messagePart.slice(0, colonIndex)).trim();
        content = messagePart.slice(colonIndex + 1).trim();
      } else {
        sender = undefined;
        content = messagePart.trim();
      }

      if (!content && currentEntry) {
        // Empty content likely indicates continuation of previous message
        const updatedEntry: ParsedMessage = {
          ...currentEntry,
          content: `${currentEntry.content}\n${trimmedLine}`,
          originalLine: `${currentEntry.originalLine}\n${rawLine}`,
        };
        if (entries.length > 0) {
          entries[entries.length - 1] = updatedEntry;
        } else {
          entries.push(updatedEntry);
        }
        currentEntry = updatedEntry;
        continue;
      }

      commitNewEntry({
        datePart,
        timePart,
        sender,
        content,
        originalLine: rawLine,
      });
    }

    const parts: Array<{
      type: "text" | "media";
      content: string;
      timestamp?: string;
      date?: string;
      time?: string;
      sender?: string;
      isForwarded?: boolean;
      originalLine?: string;
      fullTimestamp?: string;
    }> = [];

    for (const entry of entries) {
      const { isoDate, isoTimestamp, displayTime } = parseDateTime(
        entry.datePart,
        entry.timePart
      );

      const common = {
        timestamp: isoTimestamp,
        fullTimestamp: isoTimestamp,
        date: isoDate,
        time: displayTime || entry.timePart?.trim(),
        sender: entry.sender?.trim(),
        isForwarded: /forwarded|mensagem encaminhada|mensaje reenviado/i.test(
          entry.content
        ),
        originalLine: entry.originalLine,
      };

      const mediaInfo = extractMedia(entry.content);

      if (mediaInfo) {
        const remainder = mediaInfo.remainder?.trim();
        if (remainder) {
          parts.push({
            ...common,
            type: "text",
            content: remainder,
          });
        }

        parts.push({
          ...common,
          type: "media",
          content: mediaInfo.reference,
        });
      } else {
        const cleanedContent = removeDirectionMarks(entry.content).trim();
        if (cleanedContent) {
          parts.push({
            ...common,
            type: "text",
            content: cleanedContent,
          });
        }
      }
    }

    return {
      title: firstSender
        ? `WhatsApp Chat with ${firstSender}`
        : "WhatsApp Chat",
      parts,
    };
  };

  // Legacy function removed - using parseWhatsAppChatWithMediaNew instead

  // Helper: Find matching file for media reference
  const findMatchingFile = (
    mediaReference: string,
    mediaFiles: File[]
  ): File | null => {
    console.log(`🔍 Finding match for: "${mediaReference}"`);
    console.log(
      `📁 Available files:`,
      mediaFiles.map((f) => f.name)
    );

    // Clean up the media reference
    const cleanReference = mediaReference
      .replace(/^‎?<attached:\s*/, "") // Remove WhatsApp "attached:" prefix
      .replace(/>$/, ""); // Remove trailing ">"

    console.log(`🧹 Cleaned reference: "${cleanReference}"`);

    // Try exact match first
    let file = mediaFiles.find((f) => f.name === cleanReference);
    if (file) {
      console.log(`✅ Found exact match: "${file.name}"`);
      return file;
    }

    // Try case-insensitive match
    file = mediaFiles.find(
      (f) => f.name.toLowerCase() === cleanReference.toLowerCase()
    );
    if (file) {
      console.log(`✅ Found case-insensitive match: "${file.name}"`);
      return file;
    }

    // Try partial match - check if the reference contains part of the filename
    file = mediaFiles.find((f) => {
      const fileName = f.name.toLowerCase();
      const reference = cleanReference.toLowerCase();

      // Check if reference contains a significant part of the filename
      const fileNameParts = fileName.split(/[-._]/);
      const referenceParts = reference.split(/[-._]/);

      // Look for matching parts
      const matchingParts = fileNameParts.filter(
        (part) =>
          part.length > 3 &&
          referenceParts.some(
            (refPart) =>
              refPart.length > 3 &&
              (part.includes(refPart) || refPart.includes(part))
          )
      );

      if (matchingParts.length > 0) {
        console.log(
          `🔍 Partial match found: "${
            f.name
          }" (matching parts: ${matchingParts.join(", ")})`
        );
        return true;
      }

      // Also check if filename contains reference or vice versa
      if (fileName.includes(reference) || reference.includes(fileName)) {
        console.log(`🔍 Substring match found: "${f.name}"`);
        return true;
      }

      return false;
    });

    if (file) {
      console.log(`✅ Found partial match: "${file.name}"`);
      return file;
    }

    // Try date-based matching for files with dates
    const dateMatch = cleanReference.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      const targetDate = dateMatch[1];
      file = mediaFiles.find((f) => f.name.includes(targetDate));
      if (file) {
        console.log(
          `✅ Found date-based match: "${file.name}" for date: ${targetDate}`
        );
        return file;
      }
    }

    // Try matching by removing spaces and special characters (normalized matching)
    const normalizedReference = cleanReference
      .replace(/[^a-zA-Z0-9.]/g, "")
      .toLowerCase();
    file = mediaFiles.find((f) => {
      const normalizedFileName = f.name
        .replace(/[^a-zA-Z0-9.]/g, "")
        .toLowerCase();
      if (normalizedFileName === normalizedReference) {
        console.log(`✅ Found normalized match: "${f.name}"`);
        return true;
      }
      return false;
    });

    if (file) {
      return file;
    }

    // Try matching by file extension
    const ext = cleanReference.split(".").pop()?.toLowerCase();
    if (ext) {
      const filesWithExt = mediaFiles.filter((f) =>
        f.name.toLowerCase().endsWith(`.${ext}`)
      );

      if (filesWithExt.length === 1) {
        console.log(
          `✅ Found unique file with extension .${ext}: "${filesWithExt[0].name}"`
        );
        return filesWithExt[0];
      } else if (filesWithExt.length > 1) {
        console.log(
          `⚠️ Multiple files with extension .${ext} found, using first one`
        );
        return filesWithExt[0];
      }
    }

    console.log(`❌ No match found for: "${cleanReference}"`);
    console.log(
      `📁 Available files were:`,
      mediaFiles.map((f) => f.name)
    );
    return null;
  };

  // Helper: Detect media type from file
  const detectMediaType = (file: File): BlogMediaType | null => {
    // Detect type from MIME type
    if (
      file.type.startsWith("image/") ||
      file.type === "image/tiff" ||
      file.type === "image/tif" ||
      file.type === "image/x-tiff" ||
      file.type === "image/tiff-fx"
    )
      return "photo";
    else if (file.type.startsWith("video/")) return "video";
    else if (file.type === "application/pdf") return "pdf";
    else if (file.type.startsWith("audio/")) return "audio";

    // Fallback by extension
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "tif"].includes(
        ext || ""
      )
    )
      return "photo";
    else if (
      ["mp4", "avi", "mov", "wmv", "3gp", "mkv", "flv", "webm"].includes(
        ext || ""
      )
    )
      return "video";
    else if (["pdf"].includes(ext || "")) return "pdf";
    else if (
      ["mp3", "wav", "ogg", "opus", "m4a", "aac", "flac"].includes(ext || "")
    )
      return "audio";
    // Additional file types - treat as PDF for now (documents)
    else if (
      [
        "vcf", // Contact files
        "xlsx",
        "xls", // Excel files
        "docx",
        "doc", // Word documents
        "pptx",
        "ppt", // PowerPoint files
        "txt", // Text files
        "csv", // CSV files
        "rtf", // Rich text files
        "odt",
        "ods",
        "odp", // OpenDocument files
        "pages",
        "numbers",
        "keynote", // Apple iWork files
        "zip",
        "rar",
        "7z", // Archive files
        "json",
        "xml", // Data files
        "html",
        "htm", // Web files
      ].includes(ext || "")
    )
      return "pdf"; // Use PDF endpoint for document uploads

    return null;
  };

  // Enhanced WhatsApp import handler with individual file processing
  const handleWhatsAppImport = async (files: FileList) => {
    setImportError(null);
    setImportProgress("");
    setImporting(true);

    try {
      console.log(
        "🚨🚨🚨 V5.0 HANDLE WHATSAPP IMPORT EXECUTING! NUCLEAR CACHE BUST! 🚨🚨🚨"
      );
      console.log("Enhanced WhatsApp import started");
      const fileArray = Array.from(files);
      console.log(
        "Files selected:",
        fileArray.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        }))
      );

      const txtFile = fileArray.find((f) => f.name.endsWith(".txt"));
      if (!txtFile) {
        setImportError("Please select a .txt file.");
        return;
      }

      const mediaFiles = fileArray.filter((f) => !f.name.endsWith(".txt"));
      console.log(
        "Media files found:",
        mediaFiles.map((f) => f.name)
      );

      setImportProgress(
        `Found ${mediaFiles.length} media files. Parsing text file...`
      );

      // Parse the text file first
      const text = await txtFile.text();
      console.log("=== WHATSAPP IMPORT DEBUG ===");
      console.log("Raw WhatsApp text (first 1000 chars):");
      console.log(text.substring(0, 1000));
      console.log("=== END RAW TEXT ===");

      // Show a few sample lines
      const lines = text.split(/\r?\n/).slice(0, 10);
      console.log("First 10 lines of WhatsApp export:");
      lines.forEach((line, index) => {
        console.log(`Line ${index + 1}: "${line}"`);
      });

      console.log("Using WhatsApp parsing helper (V5)");
      const parsed = parseWhatsAppChatWithMediaV5(text);
      console.log("🚨 NEW PARSING COMPLETE:", parsed);
      console.log("=== PARSING RESULTS ===");
      console.log("Parsed WhatsApp chat:", parsed);
      console.log(
        "Text parts found:",
        parsed.parts.filter((p) => p.type === "text").length
      );
      console.log(
        "Media parts found:",
        parsed.parts.filter((p) => p.type === "media").length
      );

      // Show first few parsed parts
      console.log("First 5 parsed parts:");
      parsed.parts.slice(0, 5).forEach((part, index) => {
        console.log(`Part ${index + 1}:`, part);
      });

      // Show all media parts found
      console.log("All media parts found:");
      const mediaParts = parsed.parts.filter((p) => p.type === "media");
      console.log(`Total media parts: ${mediaParts.length}`);
      mediaParts.forEach((part, index) => {
        console.log(`Media ${index + 1}: "${part.content}"`);
      });
      console.log("=== END DEBUG ===");

      setImportProgress(
        `Found ${
          parsed.parts.filter((p) => p.type === "media").length
        } media references. Starting uploads...`
      );

      setTitle(parsed.title);
      let newContent = "";
      let newMedia: BlogMediaItem[] = [];
      const successfulFiles: string[] = [];
      const skippedFiles: string[] = [];
      const failedFiles: string[] = [];

      // Process each part in the exact order from the chat
      let processedCount = 0;
      const totalMedia = parsed.parts.filter((p) => p.type === "media").length;
      let currentDate = "";

      // Debug: Show all parsed parts
      console.log(`🎯 Total parts to process: ${parsed.parts.length}`);
      console.log(`📊 Parts breakdown:`, {
        text: parsed.parts.filter((p) => p.type === "text").length,
        media: parsed.parts.filter((p) => p.type === "media").length,
        forwarded: parsed.parts.filter((p) => p.isForwarded).length,
      });

      parsed.parts.forEach((part, index) => {
        console.log(`📋 Part ${index + 1}:`, {
          type: part.type,
          content:
            part.content?.substring(0, 50) +
            (part.content?.length > 50 ? "..." : ""),
          isForwarded: part.isForwarded,
          time: part.time,
          sender: part.sender,
        });
      });

      for (const part of parsed.parts) {
        // Add date header when date changes
        if (part.date && part.date !== currentDate) {
          currentDate = part.date;
          const formattedDate = new Date(part.date).toLocaleDateString(
            "en-US",
            {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          );
          newContent += `\n## 📅 ${formattedDate}\n\n`;
        }

        if (part.type === "text") {
          // Debug logging for content generation
          console.log(`📝 Processing text part:`, {
            content: part.content,
            isForwarded: part.isForwarded,
            time: part.time,
            sender: part.sender,
          });

          // Add text message with timestamp and sender
          if (part.time && part.sender) {
            if (part.isForwarded) {
              console.log(
                `📤 Adding forwarded message with timestamp: "${part.content}"`
              );
              newContent += `**${part.time} - ${part.sender}:** Forwarded: ${part.content}\n\n`;
            } else {
              console.log(
                `📝 Adding regular message with timestamp: "${part.content}"`
              );
              newContent += `**${part.time} - ${part.sender}:** ${part.content}\n\n`;
            }
          } else {
            if (part.isForwarded) {
              console.log(
                `📤 Adding forwarded message without timestamp: "${part.content}"`
              );
              newContent += `Forwarded: ${part.content}\n\n`;
            } else {
              console.log(
                `📝 Adding regular message without timestamp: "${part.content}"`
              );
              newContent += `${part.content}\n\n`;
            }
          }
        } else if (part.type === "media") {
          processedCount++;
          console.log(`Processing media reference: "${part.content}"`);
          console.log(
            `Available media files:`,
            mediaFiles.map((f) => f.name)
          );

          const file = findMatchingFile(part.content, mediaFiles);

          // Enhanced debugging for July 4th issue
          if (
            part.content.includes("actividad") ||
            part.content.includes("00000979")
          ) {
            console.log("🎯 JULY 4TH FILE MATCHING DEBUG:");
            console.log("🎯 Media reference:", part.content);
            console.log(
              "🎯 Available files:",
              mediaFiles.map((f) => f.name)
            );
            console.log("🎯 File found:", file ? file.name : "NOT FOUND");
            console.log("🎯 File type:", file ? file.type : "N/A");
            console.log("🎯 Timestamp:", part.timestamp);
            console.log("🎯 Date:", part.date);
            console.log("🎯 Time:", part.time);
          }

          setImportProgress(
            `Processing ${processedCount}/${totalMedia}: ${part.content} (${
              file
                ? `${(file.size / (1024 * 1024)).toFixed(1)}MB`
                : "unknown size"
            })`
          );

          if (file) {
            console.log(
              `Found matching file: "${file.name}" for reference: "${part.content}"`
            );

            const type = detectMediaType(file);
            if (!type) {
              console.log(`Skipping unsupported file: ${file.name}`);
              skippedFiles.push(`${file.name} (unsupported format)`);
              if (part.time && part.sender) {
                if (part.isForwarded) {
                  newContent += `**${part.time} - ${part.sender}:** Forwarded: [skipped unsupported: ${file.name}]\n\n`;
                } else {
                  newContent += `**${part.time} - ${part.sender}:** [skipped unsupported: ${file.name}]\n\n`;
                }
              } else {
                if (part.isForwarded) {
                  newContent += `Forwarded: [skipped unsupported: ${file.name}]\n\n`;
                } else {
                  newContent += `[skipped unsupported: ${file.name}]\n\n`;
                }
              }
              continue;
            }

            // Determine endpoint based on file type
            let endpoint = "";
            if (type === "photo") endpoint = "/api/upload/image";
            else if (type === "video") endpoint = "/api/upload/video";
            else if (type === "pdf") endpoint = "/api/upload/pdf";
            else if (type === "audio") endpoint = "/api/upload/audio";

            // For audio files, check if we should skip transcription
            let skipTranscription = false;
            if (type === "audio") {
              const maxSizeForTranscription = 50 * 1024 * 1024; // 50MB
              if (file.size > maxSizeForTranscription) {
                skipTranscription = true;
                console.log(
                  `Audio file ${file.name} is ${file.size} bytes, skipping transcription`
                );
              }
            }

            try {
              // Validate file before upload
              const validationError = validateFile(file, type);
              if (validationError) {
                console.log(
                  `Validation failed for ${file.name}: ${validationError}`
                );
                skippedFiles.push(`${file.name} (${validationError})`);
                if (part.time && part.sender) {
                  if (part.isForwarded) {
                    newContent += `**${part.time} - ${part.sender}:** Forwarded: [skipped validation: ${file.name} - ${validationError}]\n\n`;
                  } else {
                    newContent += `**${part.time} - ${part.sender}:** [skipped validation: ${file.name} - ${validationError}]\n\n`;
                  }
                } else {
                  if (part.isForwarded) {
                    newContent += `Forwarded: [skipped validation: ${file.name} - ${validationError}]\n\n`;
                  } else {
                    newContent += `[skipped validation: ${file.name} - ${validationError}]\n\n`;
                  }
                }
                continue;
              }

              console.log(
                `Uploading ${file.name} (type: ${type}, size: ${file.size} bytes)`
              );

              // Try uploading with retries
              let uploadAttempt = 0;
              const maxAttempts = 3;
              let uploadError = null;
              let data = null;

              while (uploadAttempt < maxAttempts) {
                try {
                  uploadAttempt++;
                  console.log(
                    `Upload attempt ${uploadAttempt}/${maxAttempts} for ${file.name}`
                  );

                  data = await uploadFile(
                    file,
                    endpoint,
                    part.content || file.name || "Media",
                    skipTranscription
                  );

                  // If upload succeeds, break the retry loop
                  break;
                } catch (err) {
                  uploadError = err;
                  console.error(
                    `Upload attempt ${uploadAttempt} failed for ${file.name}:`,
                    err
                  );

                  // If this is not the last attempt, wait before retrying
                  if (uploadAttempt < maxAttempts) {
                    const delay = uploadAttempt * 2000; // Exponential backoff
                    console.log(`Retrying in ${delay / 1000} seconds...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                  }
                }
              }

              // If all upload attempts failed
              if (!data) {
                throw (
                  uploadError ||
                  new Error("Upload failed after multiple attempts")
                );
              }

              // Create media item from successful upload
              let mediaData: any = {
                url: data.url || data.image_url,
                heading: part.content || file.name || "Media",
              };
              if (type === "audio")
                mediaData.transcript = data.transcript || "";

              const mediaItem = { type, data: mediaData };

              // Verify the media item has required fields
              if (!mediaData.url) {
                throw new Error("Upload response missing URL");
              }

              // Add to media array and update content
              newMedia.push(mediaItem);

              console.log(
                `📎 Created media item ${newMedia.length}:`,
                mediaItem
              );
              console.log(
                `📎 Current newMedia array length: ${newMedia.length}`
              );
              console.log(`📎 Media reference: "${part.content}"`);
              console.log(`📎 File found: "${file.name}"`);
              console.log(`📎 Timestamp: "${part.timestamp}"`);
              console.log(`📎 Date: "${part.date}"`);
              console.log(`📎 Time: "${part.time}"`);

              // Add media in chat format with timestamp and sender
              const mediaIndex = newMedia.length - 1;

              // Enhanced debugging for July 4th issue
              if (
                part.content.includes("actividad") ||
                part.content.includes("00000979")
              ) {
                console.log("🎯 JULY 4TH MEDIA REFERENCE DEBUG:");
                console.log("🎯 Media reference:", part.content);
                console.log("🎯 Media index being assigned:", mediaIndex);
                console.log(
                  "🎯 Media item at this index:",
                  newMedia[mediaIndex]
                );
                console.log("🎯 Timestamp:", part.timestamp);
                console.log("🎯 Date:", part.date);
                console.log("🎯 Time:", part.time);
              }

              if (part.time && part.sender) {
                if (part.isForwarded) {
                  newContent += `**${part.time} - ${part.sender}:** Forwarded: [media:${mediaIndex}]\n\n`;
                } else {
                  newContent += `**${part.time} - ${part.sender}:** [media:${mediaIndex}]\n\n`;
                }
              } else {
                if (part.isForwarded) {
                  newContent += `Forwarded: [media:${mediaIndex}]\n\n`;
                } else {
                  newContent += `[media:${mediaIndex}]\n\n`;
                }
              }

              successfulFiles.push(file.name);
              console.log(`✅ Successfully uploaded ${file.name}`);
            } catch (err) {
              const errorMessage =
                err instanceof Error ? err.message : "Unknown error";
              console.error(
                `❌ Failed to upload ${file.name}: ${errorMessage}`,
                err
              );
              failedFiles.push(`${file.name} (${errorMessage})`);
              if (part.time && part.sender) {
                if (part.isForwarded) {
                  newContent += `**${part.time} - ${part.sender}:** Forwarded: [failed upload: ${file.name} - ${errorMessage}]\n\n`;
                } else {
                  newContent += `**${part.time} - ${part.sender}:** [failed upload: ${file.name} - ${errorMessage}]\n\n`;
                }
              } else {
                if (part.isForwarded) {
                  newContent += `Forwarded: [failed upload: ${file.name} - ${errorMessage}]\n\n`;
                } else {
                  newContent += `[failed upload: ${file.name} - ${errorMessage}]\n\n`;
                }
              }
            }
          } else {
            console.log(`⚠️ No matching file found for: "${part.content}"`);
            if (part.time && part.sender) {
              if (part.isForwarded) {
                newContent += `**${part.time} - ${part.sender}:** Forwarded: [missing media: ${part.content}]\n\n`;
              } else {
                newContent += `**${part.time} - ${part.sender}:** [missing media: ${part.content}]\n\n`;
              }
            } else {
              if (part.isForwarded) {
                newContent += `Forwarded: [missing media: ${part.content}]\n\n`;
              } else {
                newContent += `[missing media: ${part.content}]\n\n`;
              }
            }
          }
        }
      }

      setContent(newContent.trim());
      setMedia(newMedia);

      console.log(`🎯 Final WhatsApp import results:`);
      console.log(`📝 Content length: ${newContent.length}`);
      console.log(`📝 Content preview: ${newContent.substring(0, 200)}...`);
      console.log(`📎 Media array length: ${newMedia.length}`);
      console.log(`📎 Media items:`, newMedia);
      console.log(
        `📎 Media references in content:`,
        (newContent.match(/\[media:\d+\]/g) || []).length
      );

      // Force a small delay to ensure state is updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Double-check that media state was updated
      console.log(`🔍 Verifying media state update...`);
      console.log(`📎 Current media state length: ${media.length}`);
      console.log(`📎 Expected media array length: ${newMedia.length}`);

      // If media state wasn't updated properly, force it
      if (media.length !== newMedia.length) {
        console.log(`⚠️ Media state mismatch detected, forcing update...`);
        setMedia([...newMedia]); // Force a new array reference
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Create detailed summary
      let summary = "";
      if (successfulFiles.length > 0) {
        summary += `✅ Successfully uploaded: ${successfulFiles.join(", ")}. `;
      }
      if (skippedFiles.length > 0) {
        summary += `⚠️ Skipped files: ${skippedFiles.join(", ")}. `;
      }
      if (failedFiles.length > 0) {
        summary += `❌ Failed files: ${failedFiles.join(", ")}. `;
      }

      if (summary) {
        summary += "\n\n💡 Tips: ";
        if (failedFiles.length > 0) {
          summary += "Try uploading failed files individually. ";
        }
        if (skippedFiles.length > 0) {
          summary += "Check file formats and sizes. ";
        }
        summary += "For large files, try uploading them one at a time.";
        setImportError(summary);
      }

      console.log("WhatsApp import completed:", {
        successful: successfulFiles.length,
        skipped: skippedFiles.length,
        failed: failedFiles.length,
        total: mediaFiles.length,
      });

      setImportProgress(
        `Import completed! ${successfulFiles.length} successful, ${skippedFiles.length} skipped, ${failedFiles.length} failed.`
      );
    } catch (err) {
      console.error("WhatsApp import error:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setImportError(
        `Failed to import WhatsApp chat with media: ${errorMessage}`
      );
    } finally {
      setImporting(false);
    }
  };

  // Markdown import handler
  const handleMarkdownImport = async (file: File) => {
    setMarkdownImportError(null);
    setImportingMarkdown(true);

    try {
      console.log("Markdown import started for file:", file.name);

      // Validate file size (max 10MB for markdown files)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error(
          `File size must be less than ${maxSize / (1024 * 1024)}MB`
        );
      }

      // Read the markdown file content
      const markdownContent = await file.text();

      if (!markdownContent.trim()) {
        throw new Error("The Markdown file appears to be empty");
      }

      // Extract title from the first H1 heading or use filename
      const titleMatch = markdownContent.match(/^#\s+(.+)$/m);
      const extractedTitle = titleMatch
        ? titleMatch[1].trim()
        : file.name.replace(/\.(md|markdown)$/i, "");

      // Clean up the title (remove special characters that might cause issues)
      const cleanTitle = extractedTitle.replace(/[^\w\s-]/g, "").trim();

      // Set the title and content
      setTitle(cleanTitle || "Imported Markdown Post");
      setContent(markdownContent);

      // Clear any existing media when importing markdown (markdown is content-only)
      setMedia([]);

      console.log("Markdown import completed successfully");
      console.log("Extracted title:", cleanTitle);
      console.log("Content length:", markdownContent.length);
    } catch (err) {
      console.error("Markdown import error:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setMarkdownImportError(`Failed to import Markdown file: ${errorMessage}`);
    } finally {
      setImportingMarkdown(false);
    }
  };

  // Add media handler
  const handleAddMedia = async (
    file: File,
    type: BlogMediaType,
    heading: string
  ) => {
    // Validate file
    const validationError = validateFile(file, type);
    if (validationError) {
      setError(validationError);
      return;
    }

    let endpoint = "";
    if (type === "photo") endpoint = "/api/upload/image";
    if (type === "video") endpoint = "/api/upload/video";
    if (type === "pdf") endpoint = "/api/upload/pdf";
    if (type === "audio") endpoint = "/api/upload/audio";

    // For audio files, check if we should skip transcription
    let skipTranscription = false;
    if (type === "audio") {
      const maxSizeForTranscription = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSizeForTranscription) {
        skipTranscription = true;
        console.log(
          `Audio file ${file.name} is ${file.size} bytes, skipping transcription`
        );
      }
    }

    try {
      const data = await uploadFile(file, endpoint, heading, skipTranscription);
      let item: BlogMediaItem;
      if (type === "audio") {
        item = {
          type,
          data: {
            url: data.url,
            heading: heading,
            transcript: data.transcript || "",
          },
        };
      } else {
        item = {
          type,
          data: { url: data.url || data.image_url, heading },
        };
      }

      // Validate the media item
      const itemValidation = validateMediaItem(item);
      if (itemValidation) {
        setError(itemValidation);
        return;
      }

      // Add to local state using functional update to avoid race conditions
      setMedia((prevMedia) => {
        const newMedia =
          selectedMediaIndex !== null
            ? [
                ...prevMedia.slice(0, selectedMediaIndex + 1),
                item,
                ...prevMedia.slice(selectedMediaIndex + 1),
              ]
            : [...prevMedia, item];

        // Debug log to track media array updates
        console.log(
          `Previous media count: ${prevMedia.length}, New media count: ${newMedia.length}`
        );

        return newMedia;
      });

      setMediaHeading(""); // Clear heading after successful upload
      setSelectedMediaIndex(null); // Reset after insert
      setError(null);

      // Show success message for upload only - no auto-save
      setSuccess(
        "Media uploaded successfully! Click 'Post' when ready to publish your blog post."
      );
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  };

  // Remove media handler
  const handleRemoveMedia = (idx: number) => {
    console.log(`🗑️ Removing media item at index ${idx}`);
    console.log(`📎 Current media count: ${media.length}`);

    // Check if the index is valid
    if (idx >= media.length || idx < 0) {
      console.warn(
        `⚠️ Invalid media index ${idx}, current media length: ${media.length}`
      );
      return;
    }

    setMedia((prev) => {
      const newMedia = prev.filter((_, i) => i !== idx);
      console.log(`📎 New media count: ${newMedia.length}`);
      console.log(
        `📎 Remaining media items:`,
        newMedia.map((item, i) => ({
          index: i,
          type: item.type,
          heading: item.data.heading,
        }))
      );
      return newMedia;
    });
  };

  // Edit media handler
  const handleEditMedia = (idx: number) => {
    console.log(`✏️ Editing media item at index ${idx}`);
    // For now, just set the selected media index to allow editing the heading
    setSelectedMediaIndex(idx);
    // You could also open a modal or inline editor here
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Debug log: show what will be submitted
    console.log("Submitting blog post:", { title, content, media });
    console.log(`📎 Media array length at submission: ${media.length}`);
    console.log(`📎 Media array content:`, media);

    // Validate that media array is properly set
    if (media.length === 0 && content.includes("[media:")) {
      console.warn(
        "⚠️ Warning: Content contains media references but media array is empty!"
      );
      setError(
        "Media array is empty but content contains media references. Please try importing again."
      );
      setLoading(false);
      return;
    }

    try {
      if (onSubmit) {
        const submitData: Partial<BlogPost> = { title, content, category };
        // Only include media if it has items (not empty)
        if (media && media.length > 0) {
          submitData.media = media;
        }
        await onSubmit(submitData);
        setSuccess("Blog post updated successfully!");
      } else {
        // Send data in new BlogPost format with media field
        const backendData = {
          title,
          content,
          category,
          media: media.map((item) => ({
            type: item.type,
            data: item.data,
          })),
          hidden: false,
        };

        console.log("🎯 Sending to backend:", backendData);
        console.log("📎 Media array being sent:", backendData.media);
        console.log(
          `📎 Media array length being sent: ${backendData.media.length}`
        );
        console.log(
          "📝 Content being sent:",
          content.substring(0, 200) + "..."
        );
        console.log("📝 Title being sent:", title);

        // Validate media array before sending
        if (backendData.media.length === 0 && content.includes("[media:")) {
          throw new Error(
            "Media array is empty but content contains media references. Please try importing again."
          );
        }

        const response = await fetch("/api/blog/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify(backendData),
        });

        console.log("📡 Response status:", response.status);
        console.log("📡 Response ok:", response.ok);

        if (!response.ok) {
          const data = await response.json();
          const errorMessage = data.detail || "Failed to create blog post";

          // Check if it's a duplicate title error
          if (errorMessage.includes("already exists")) {
            throw new Error(
              `❌ ${errorMessage} Please choose a different title for your blog post.`
            );
          }

          throw new Error(errorMessage);
        }

        const responseData = await response.json();
        console.log("✅ Blog post created successfully:", responseData);
        setSuccess(
          "✅ Blog post created successfully! Your WhatsApp chat has been imported and posted."
        );
        // Don't clear the form immediately - let user see the content was saved
        // setTitle("");
        // setContent("");
        // setMedia([]);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Get appropriate icon for media type
  const getMediaIcon = (type: BlogMediaType, fileName?: string) => {
    switch (type) {
      case "photo":
        return <PhotoCamera />;
      case "video":
        return <VideocamIcon />;
      case "pdf":
        // Show specific icons for different document types
        if (fileName) {
          const ext = fileName.split(".").pop()?.toLowerCase();
          if (["vcf"].includes(ext || "")) {
            return <ContactPhoneIcon />; // Contact files
          } else if (["xlsx", "xls"].includes(ext || "")) {
            return <TableChartIcon />; // Excel files
          } else if (["docx", "doc"].includes(ext || "")) {
            return <ArticleIcon />; // Word documents
          } else if (["pptx", "ppt"].includes(ext || "")) {
            return <SlideshowIcon />; // PowerPoint files
          } else if (["zip", "rar", "7z"].includes(ext || "")) {
            return <FolderZipIcon />; // Archive files
          }
        }
        return <DescriptionIcon />; // Default for PDFs and other documents
      case "audio":
        return <AudiotrackIcon />;
      default:
        return <PhotoCamera />;
    }
  };

  // Render media
  const renderMedia = (item: BlogMediaItem, idx: number) => {
    const commonStyles = {
      border: "1px solid #e0e0e0",
      borderRadius: 1,
      p: 2,
      position: "relative" as const,
    };

    switch (item.type) {
      case "photo":
        return (
          <Box key={idx} sx={commonStyles}>
            <img
              src={item.data.url}
              alt={item.data.heading}
              loading="lazy"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: 4,
                marginBottom: 8,
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/default-auction.png";
              }}
            />
            <Typography
              variant="caption"
              display="block"
              sx={{ fontWeight: "bold" }}
            >
              {item.data.heading}
            </Typography>
            <Chip
              icon={getMediaIcon(item.type, item.data.heading)}
              label="Photo"
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleEditMedia(idx)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedMediaIndex(idx)}
              >
                Insert
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={idx >= media.length}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`🔴 Delete button clicked for media item ${idx}`);
                  handleRemoveMedia(idx);
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        );
      case "video":
        return (
          <Box key={idx} sx={commonStyles}>
            <video
              src={item.data.url}
              controls
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <Typography
              variant="caption"
              display="block"
              sx={{ fontWeight: "bold" }}
            >
              {item.data.heading}
            </Typography>
            <Chip
              icon={getMediaIcon(item.type, item.data.heading)}
              label="Video"
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleEditMedia(idx)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedMediaIndex(idx)}
              >
                Insert
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={idx >= media.length}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`🔴 Delete button clicked for media item ${idx}`);
                  handleRemoveMedia(idx);
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        );
      case "pdf":
        return (
          <Box key={idx} sx={commonStyles}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <DescriptionIcon
                sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
              />
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                {item.data.heading}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                href={item.data.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DescriptionIcon />}
              >
                View PDF
              </Button>
            </Box>
            <Chip
              icon={getMediaIcon(item.type, item.data.heading)}
              label="PDF"
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleEditMedia(idx)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedMediaIndex(idx)}
              >
                Insert
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={idx >= media.length}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`🔴 Delete button clicked for media item ${idx}`);
                  handleRemoveMedia(idx);
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        );
      case "audio":
        return (
          <Box key={idx} sx={commonStyles}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              {item.data.heading}
            </Typography>
            <audio
              src={item.data.url}
              controls
              style={{ width: "100%", marginBottom: 8 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <TextField
                label="Transcript"
                value={
                  item.type === "audio" && "transcript" in item.data
                    ? item.data.transcript
                    : ""
                }
                onChange={(e) => {
                  if (item.type !== "audio" || !("transcript" in item.data))
                    return;
                  const updated = [...media];
                  updated[idx].data = {
                    ...updated[idx].data,
                    transcript: e.target.value,
                  };
                  setMedia(updated);
                }}
                fullWidth
                multiline
                rows={3}
              />
              {initialValues?.id && item.type === "audio" && (
                <TranscribeButton
                  postId={initialValues.id}
                  mediaIndex={idx}
                  userToken={user?.token}
                  onTranscript={(transcript) => {
                    if (item.type !== "audio" || !("transcript" in item.data))
                      return;
                    const updated = [...media];
                    updated[idx].data = {
                      ...updated[idx].data,
                      transcript,
                    };
                    setMedia(updated);
                  }}
                  disabled={loading}
                />
              )}
            </Box>
            <Chip
              icon={getMediaIcon(item.type, item.data.heading)}
              label="Audio"
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleEditMedia(idx)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedMediaIndex(idx)}
              >
                Insert
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={idx >= media.length}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`🔴 Delete button clicked for media item ${idx}`);
                  handleRemoveMedia(idx);
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {isEdit ? "Edit Blog Post" : "Create New Blog Post"}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            sx={{ mb: 1, mr: 2 }}
            disabled={importing}
          >
            Import WhatsApp Chat (.txt + media + docs)
            <input
              type="file"
              accept=".txt,image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) {
                  handleWhatsAppImport(e.target.files);
                }
              }}
            />
          </Button>
          <Button
            variant="outlined"
            component="label"
            sx={{ mb: 1, mr: 2 }}
            disabled={importingMarkdown}
            startIcon={<TextFieldsIcon />}
          >
            Import Markdown File (.md)
            <input
              type="file"
              accept=".md,.markdown"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleMarkdownImport(file);
                }
              }}
            />
          </Button>
          {importing && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {importProgress || "Processing..."}
              </Typography>
            </Box>
          )}
          {importingMarkdown && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Importing Markdown file...
              </Typography>
            </Box>
          )}
          {importError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {importError}
            </Alert>
          )}
          {markdownImportError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {markdownImportError}
            </Alert>
          )}
        </Box>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          sx={{ mb: 1 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 2, display: "block" }}
        >
          💡 Each blog post title must be unique. If you see an error about
          duplicate titles, please choose a different title.
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="Project">Project</MenuItem>
            <MenuItem value="WhatsApp">WhatsApp</MenuItem>
            <MenuItem value="News">News</MenuItem>
            <MenuItem value="Secret">Secret</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          fullWidth
          required
          multiline
          minRows={4}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Media
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Tip: Upload all your media first, then click 'Post' to publish your
            blog post with all media included. No auto-save - your post will
            only be published when you click 'Post'.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="media-type-label">Media Type</InputLabel>
              <Select
                labelId="media-type-label"
                value={mediaType}
                label="Media Type"
                onChange={(e) => setMediaType(e.target.value as BlogMediaType)}
              >
                <MenuItem value="photo">Photo</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Media Heading"
              value={mediaHeading}
              onChange={(e) => setMediaHeading(e.target.value)}
              placeholder="Enter a heading for this media"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={getMediaIcon(mediaType)}
              disabled={uploadingFiles.size > 0}
            >
              {uploadingFiles.size > 0 ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {`Add ${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}`}
              <input
                type="file"
                accept={
                  mediaType === "photo"
                    ? "image/*"
                    : mediaType === "video"
                    ? "video/*"
                    : mediaType === "pdf"
                    ? "application/pdf"
                    : "audio/*"
                }
                hidden
                multiple
                onChange={async (e) => {
                  if (e.target.files) {
                    for (const file of Array.from(e.target.files)) {
                      await handleAddMedia(file, mediaType, mediaHeading || "");
                    }
                  }
                }}
              />
            </Button>
          </Box>
        </Box>

        {media.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Media ({media.length})
            </Typography>
            <Grid container spacing={2}>
              {media.map((item, idx) => {
                console.log(`🎨 Rendering media item ${idx}:`, {
                  type: item.type,
                  heading: item.data.heading,
                });
                return (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    {renderMedia(item, idx)}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
        {media.length === 0 && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              border: "1px dashed #ccc",
              borderRadius: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No media items. Upload some media above to get started.
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => {
              setTitle("");
              setContent("");
              setMedia([]);
              setError(null);
              setSuccess(null);
            }}
          >
            Clear Form
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || uploadingFiles.size > 0}
          >
            {loading
              ? isEdit
                ? "Saving..."
                : "Posting..."
              : isEdit
              ? "Save"
              : "Post"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default BlogCreate;
